import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Package, Clock, Bookmark, SlidersHorizontal, CreditCard, LogOut, Menu, X, ChevronUp, LayoutDashboard, Gavel, Wallet, Film, LifeBuoy } from 'lucide-react';
import Logo from '@/components/Logo';
import CreditsBadge from '@/components/CreditsBadge';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { useAuth } from '@/context/AuthContext';

const NAV = [
  { label: 'Dashboard', to: '/creator-dashboard', match: '/creator-dashboard', icon: LayoutDashboard },
  { label: 'Products', to: '/dashboard', match: '/dashboard', icon: Package },
  { label: 'Videos', to: '/videos', match: '/videos', icon: Film },
  { label: 'History', to: '/dashboard?tab=history', match: '__history__', icon: Clock },
  { label: 'Saved ideas', to: '/saved', match: '/saved', icon: Bookmark },
  { label: 'Violation wizard', to: '/violation-wizard', match: '/violation-wizard', icon: Gavel },
  { label: 'Income calculator', to: '/income-calculator', match: '/income-calculator', icon: Wallet },
  { label: 'Help & support', to: '/help', match: '/help', icon: LifeBuoy },
];

const SidebarInner = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenu, setUserMenu] = useState(false);

  const planLabel = user?.is_admin
    ? 'Admin plan'
    : user?.is_pro
      ? 'Pro plan'
      : user?.is_basic
        ? 'Basic plan'
        : 'Free trial';
  const displayName = user?.name || user?.email?.split('@')[0] || 'Your account';
  const initial = (displayName || 'P').charAt(0).toUpperCase();

  const isActive = (item) => {
    if (item.match === '__history__') return location.pathname === '/dashboard' && location.search.includes('history');
    if (item.match === '/dashboard') return location.pathname === '/dashboard' && !location.search.includes('history');
    return location.pathname === item.match;
  };

  const handleLogout = () => { logout(); toast.success('Logged out'); navigate('/'); };

  const navItemCls = (active) =>
    `flex items-center gap-3 h-[38px] px-3 rounded-[10px] text-sm transition-colors ${
      active
        ? 'bg-[#FFFDF9] border border-[#E6DED1] text-[#1F1B16] font-medium'
        : 'text-[#5E574E] hover:bg-[#EFE7DB]/60 border border-transparent'
    }`;

  return (
    <div className="flex h-full flex-col px-4 py-5">
      <Link to="/" onClick={onNavigate} className="px-1 mb-6">
        <Logo />
      </Link>

      <nav className="flex flex-col gap-1 mt-6">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.label} to={item.to} onClick={onNavigate} className={navItemCls(isActive(item))} data-testid={`shell-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="flex-1" />

      {/* Earn with Poettik card */}
      <Link to="/affiliate-interest" onClick={onNavigate} data-testid="shell-earn-card" className="block rounded-[14px] bg-[#EFE7DB] p-4 mb-3 hover:brightness-[0.98] transition">
        <p className="font-display text-base leading-none mb-1.5">Earn with Poettik</p>
        <p className="text-xs text-[#6F675D] leading-snug">Refer creators and earn on every sign-up.</p>
      </Link>

      <Link to="/profile" onClick={onNavigate} className={navItemCls(location.pathname === '/profile')} data-testid="shell-nav-settings">
        <SlidersHorizontal className="w-4 h-4 shrink-0" strokeWidth={1.6} />
        Settings
      </Link>

      {/* User row + menu */}
      <div className="relative mt-3 pt-3 border-t border-[#E6DED1]">
        {userMenu && (
          <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl bg-[#FFFDF9] border border-[#E6DED1] shadow-[0_12px_32px_rgba(70,45,20,0.10)] p-1.5 z-10" data-testid="shell-user-menu">
            <Link to="/subscription" onClick={() => { setUserMenu(false); onNavigate?.(); }} className="flex items-center gap-2.5 px-3 h-9 rounded-lg text-sm text-[#5E574E] hover:bg-[#EFE7DB]/60">
              <CreditCard className="w-4 h-4" strokeWidth={1.6} /> Subscription
            </Link>
            <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 h-9 rounded-lg text-sm text-[#5E574E] hover:bg-[#EFE7DB]/60" data-testid="shell-logout">
              <LogOut className="w-4 h-4" strokeWidth={1.6} /> Log out
            </button>
          </div>
        )}
        <button onClick={() => setUserMenu((v) => !v)} className="w-full flex items-center gap-3 px-1 py-1 rounded-lg hover:bg-[#EFE7DB]/50 transition-colors" data-testid="shell-user-row">
          <span className="w-8 h-8 rounded-full bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center font-semibold text-sm shrink-0">{initial}</span>
          <span className="flex-1 text-left leading-tight overflow-hidden">
            <span className="block text-sm font-semibold text-[#1F1B16] truncate">{displayName}</span>
            <span className="block text-xs text-[#6F675D] truncate" data-testid="sidebar-plan-label">{planLabel}</span>
          </span>
          <ChevronUp className={`w-4 h-4 text-[#6F675D] transition-transform ${userMenu ? '' : 'rotate-180'}`} />
        </button>
      </div>
    </div>
  );
};

// Fixed bottom tab bar for mobile — native app feel. Four thumb-reachable
// destinations: Dashboard, Products, Settings, Log out.
const MobileBottomNav = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); toast.success('Logged out'); navigate('/'); };

  const tabs = [
    { label: 'Dashboard', to: '/creator-dashboard', icon: LayoutDashboard, active: location.pathname === '/creator-dashboard' },
    { label: 'Products', to: '/dashboard', icon: Package, active: location.pathname === '/dashboard' },
    { label: 'Settings', to: '/profile', icon: SlidersHorizontal, active: location.pathname === '/profile' },
  ];

  const tabCls = (active) =>
    `flex flex-col items-center justify-center gap-1 flex-1 h-16 text-[10px] font-medium leading-none transition-colors ${
      active ? 'text-[#9D174D]' : 'text-[#6F675D] active:text-[#1F1B16]'
    }`;

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FFFDF9]/95 backdrop-blur border-t border-[#E6DED1] pb-[env(safe-area-inset-bottom)]"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-stretch justify-around h-16">
        {tabs.map(({ label, to, icon: Icon, active }) => (
          <Link key={label} to={to} className={tabCls(active)} data-testid={`bottomnav-${label.toLowerCase()}`}>
            <Icon className="w-[26px] h-[26px]" strokeWidth={active ? 2.2 : 1.8} />
            {label}
          </Link>
        ))}
        <button onClick={handleLogout} className={tabCls(false)} data-testid="bottomnav-log-out">
          <LogOut className="w-[26px] h-[26px]" strokeWidth={1.8} />
          Log out
        </button>
      </div>
    </nav>
  );
};

// Sidebar shell for logged-in pages. Presentational wrapper — pass page content
// as children. `title` shows top-left on the paper sheet; `right` is an optional
// top-right node (e.g. credits remaining).
const AppShell = ({ children, title, right }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F4EFE7]">
      {/* Desktop sidebar */}
      <aside className="hidden md:block fixed top-0 left-0 h-screen w-[232px] bg-[#F4EFE7]" data-testid="app-sidebar">
        <SidebarInner />
      </aside>

      {/* Mobile top bar — sticky, larger logo, bolder burger */}
      <div className="md:hidden sticky top-0 z-40 flex items-center justify-between h-16 px-4 bg-[#F4EFE7]/95 backdrop-blur border-b border-[#E6DED1]">
        <Link to="/"><Logo size="lg" /></Link>
        <button onClick={() => setMobileOpen(true)} className="p-2 -mr-1 rounded-xl text-[#1F1B16] hover:bg-[#EFE7DB] active:bg-[#E6DED1] transition-colors" data-testid="shell-mobile-open" aria-label="Open menu">
          <Menu className="w-7 h-7" strokeWidth={2.5} />
        </button>
      </div>

      {/* Mobile drawer — flies in from the right, 4/5 width, fits visible viewport */}
      <div
        className={`md:hidden fixed inset-0 z-[100] bg-black/40 transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileOpen(false)}
        data-testid="shell-mobile-backdrop"
      />
      <aside
        className={`md:hidden fixed top-0 right-0 z-[101] h-[100dvh] w-[80vw] max-w-[360px] bg-[#F4EFE7] border-l border-[#E6DED1] shadow-2xl overflow-y-auto transform transition-transform duration-300 ease-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
        data-testid="shell-mobile-drawer"
      >
        <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 p-1.5 rounded-lg text-[#1F1B16] hover:bg-[#EFE7DB]" aria-label="Close menu" data-testid="shell-mobile-close">
          <X className="w-5 h-5" />
        </button>
        <SidebarInner onNavigate={() => setMobileOpen(false)} />
      </aside>

      {/* Paper sheet */}
      <main className="md:pl-[232px] pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
        <div className="m-2 sm:m-3 rounded-[18px] sm:rounded-[22px] border border-[#E6DED1] bg-[#FFFDF9] min-h-[calc(100vh-16px)] sm:min-h-[calc(100vh-24px)] overflow-hidden">
          {(title || right) && (
            <header className="flex items-center justify-between gap-3 h-16 px-4 sm:px-8 border-b border-[#EFE8DD]">
              <h1 className="text-lg text-[#1F1B16] font-display truncate">{title}</h1>
              <div className="text-sm text-[#6F675D] shrink-0">{right ?? <CreditsBadge />}</div>
            </header>
          )}
          {children}
        </div>
      </main>

      {/* Mobile bottom tab bar + install prompt */}
      <MobileBottomNav />
      <PWAInstallPrompt />
    </div>
  );
};

export default AppShell;
